import { useEffect, useRef, useState, type DependencyList } from "react";

// For a sideways-scrolling table wrapper with pinned (sticky) first/last columns: tracks
// whether there is anywhere left to scroll in each direction, so the scroll arrows show only
// while they would do something, and offers one-click jumps to either edge.
export function useHorizontalScrollEdges<T extends HTMLElement = HTMLDivElement>(deps: DependencyList = []) {
  const ref = useRef<T>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const scrollToStart = () => ref.current?.scrollTo({ left: 0, behavior: "smooth" });
  const scrollToEnd   = () => ref.current?.scrollTo({ left: ref.current.scrollWidth, behavior: "smooth" });

  return { ref, canScrollLeft, canScrollRight, scrollToStart, scrollToEnd };
}
